import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*
 Il titolo della finestra e' l'unica etichetta che l'utente vede quando la pagina non e'
 in primo piano: nei preferiti, nella cronologia, fra venti schede aperte. Con un titolo
 statico tutte le schede del portale si chiamano allo stesso modo e non si distinguono.

 La mappa sta qui e non nelle pagine per una ragione pratica: le rotte sono dichiarate
 tutte insieme in `App.tsx`, e una sola tabella accanto al punto in cui si monta il router
 e' piu' facile da tenere allineata di dodici chiamate sparse nelle pagine.
*/

const NOME_APPLICAZIONE = "Sempre Formati";

// L'ordine conta: si prende la prima rotta che combacia, quindi le piu' specifiche
// stanno prima (`/login/firstLogin` prima di `/login`). `parziale` vale per le rotte
// che proseguono con altri segmenti (il dettaglio corso ne ha fino a tre).
const TITOLI_PER_ROTTA: ReadonlyArray<{
  rotta: string;
  chiave: string;
  parziale?: boolean;
}> = [
  { rotta: "/login/firstLogin", chiave: "titolo-pagina-primo-accesso" },
  { rotta: "/login", chiave: "titolo-pagina-accesso" },
  {
    rotta: "/reset-password/change-password/:token",
    chiave: "titolo-pagina-nuova-password",
  },
  { rotta: "/reset-password", chiave: "titolo-pagina-reset-password" },
  { rotta: "/home", chiave: "titolo-pagina-home" },
  { rotta: "/dashboard", chiave: "titolo-pagina-dashboard" },
  { rotta: "/user", chiave: "titolo-pagina-profilo" },
  { rotta: "/attestati", chiave: "titolo-pagina-attestati" },
  {
    rotta: "/previous-certificates",
    chiave: "titolo-pagina-attestati-pregressi",
  },
  { rotta: "/training-needs", chiave: "titolo-pagina-fabbisogno" },
  { rotta: "/prenota-un-corso", chiave: "titolo-pagina-prenota-corso" },
  { rotta: "/corsi-prenotati", chiave: "titolo-pagina-corsi-prenotati" },
  { rotta: "/corso", chiave: "titolo-pagina-corso", parziale: true },
  { rotta: "/anagrafica-dipendenti", chiave: "titolo-pagina-dipendenti" },
  { rotta: "/500", chiave: "titolo-pagina-errore" },
  { rotta: "/", chiave: "titolo-pagina-accesso" },
];

export function chiaveTitoloPerPercorso(percorso: string): string {
  const trovata = TITOLI_PER_ROTTA.find(({ rotta, parziale }) =>
    matchPath({ path: rotta, end: !parziale }, percorso)
  );
  // Quello che non combacia con nessuna rotta e' cio' che in `App.tsx` finisce su `*`.
  return trovata?.chiave ?? "titolo-pagina-non-trovata";
}

export function useTitoloPagina(): void {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const titolo = t(chiaveTitoloPerPercorso(pathname));
    document.title = `${titolo} - ${NOME_APPLICAZIONE}`;
  }, [pathname, t]);
}
